export const ARCHIVE_ROOT = "archive";
export const MAX_NOTE_BYTES = 1024 * 1024;
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
// Measured as stored: ASCII armor makes an encrypted file about a third larger than its contents.
export const MAX_ENCRYPTED_FILE_BYTES = 10 * 1024 * 1024;
/** The passphrase-protected age identity that makes its folder an encrypted folder. */
export const FOLDER_KEY_NAME = ".age-identity";
export const MAX_VAULT_FILES = 2000;
export const MAX_VAULT_BYTES = 20 * 1024 * 1024;
export const MAX_CHANGED_FILES = MAX_VAULT_FILES * 2;
export const MAX_REQUEST_BYTES = 64 * 1024 * 1024; // JSON escaping of the bounded vault
export type ErrorCode =
  | "unauthorized"
  | "csrf"
  | "invalid_request"
  | "offline"
  | "revoked_access"
  | "protected_branch"
  | "stale_base"
  | "conflict"
  | "rate_limit"
  | "invalid_vault"
  | "snapshot_incomplete"
  | "vault_too_large"
  | "checkpoint_unknown"
  | "branch_missing";
export class VaultError extends Error {
  constructor(
    public code: ErrorCode,
    message: string,
    public status = 400,
    public recoveryToken?: string,
  ) {
    super(message);
  }
}
export interface RepositoryMetadata {
  pinned: string[];
  checkpoint: { auto_minutes: number };
  /** Folder path → its icon: a built-in icon's id, or `custom:` and an uploaded one's. */
  icons: Record<string, string>;
  /** Uploaded icons by id, each a small PNG data URL. */
  custom_icons: Record<string, string>;
}
export interface MetadataPatch {
  auto_minutes?: number;
  pins?: Record<string, boolean>;
  // null forgets an entry, as D1's json_patch does.
  icons?: Record<string, string | null>;
  custom_icons?: Record<string, string | null>;
}
export interface Connection {
  repositoryId: number;
  installationId: number;
  owner: string;
  repository: string;
  branch: string;
}
export type FileState =
  | { exists: false }
  | { exists: true; text: string; sha?: string };
export const absent: FileState = { exists: false };
export const present = (text: string, sha?: string): FileState => ({
  exists: true,
  text,
  ...(sha ? { sha } : {}),
});
export function sameFile(a: FileState, b: FileState): boolean {
  return (
    a.exists === b.exists && (!a.exists || (b.exists && a.text === b.text))
  );
}
export interface DocumentRecord {
  vaultId: string;
  path: string;
  text: string;
  archived: boolean;
  base: FileState;
  dirty: boolean;
  revision: number;
  editedAt?: number;
}
export interface Operation {
  vaultId: string;
  seq: number;
  kind: "create" | "update" | "delete" | "move";
  changes: FileChange[];
}
export interface FileChange {
  path: string;
  before: FileState;
  after: FileState;
  base?: FileState;
}
export interface Attempt {
  vaultId: string;
  id: string;
  target: Connection;
  epoch: number;
  baseCommitSha: string | null;
  watermark: number;
  message: string;
  changes: FileChange[];
  state: "submitted" | "acknowledged" | "rejected";
  createdAt: number;
  recoveryToken?: string;
}
export interface Snapshot {
  commitSha: string | null;
  files: Record<string, FileState>;
  folders?: string[];
}
export interface CheckpointResult {
  attemptId: string;
  target: Connection;
  commitSha: string;
  unchanged: boolean;
  files: Record<string, FileState>;
  folders?: string[];
}
export interface Conflict {
  id: string;
  paths: string[];
  reason: string;
  base: Record<string, FileState>;
  local: Record<string, FileState>;
  remote: Record<string, FileState>;
}
export interface Recovery {
  vaultId: string;
  id: string;
  path: string;
  createdAt: number;
  reason: string;
  file: FileState;
  conflict?: Conflict;
  resolved: boolean;
}
export interface VaultRecord {
  id: string;
  generation: number;
  sequence: number;
  epoch: number;
  fence: number;
  lease: { owner: string; expires: number; browserManaged?: boolean } | null;
  connection: Connection | null;
  baseCommitSha: string | null;
  lastCheckpoint: number;
  createdAt: number;
  updatedAt: number;
  pathRevisions: Record<string, number>;
  repositoryFolders?: string[];
  userId?: number;
  remoteLoaded?: boolean;
}
export interface MetadataRecord {
  pending?: MetadataPatch;
  repositories?: Record<
    string,
    { value: RepositoryMetadata; pending: MetadataPatch }
  >;
  vaultId: string;
  value: RepositoryMetadata;
}
export interface VaultState {
  vault: VaultRecord;
  documents: DocumentRecord[];
  metadata: MetadataRecord;
  operations: Operation[];
  attempts: Attempt[];
  recovery: Recovery[];
}
export interface Guard {
  generation: number;
  epoch: number;
  fence: number;
  baseCommitSha: string | null;
}
export interface SessionInfo {
  user: { id?: number; login: string; avatarUrl: string } | null;
  csrf: string | null;
  connection: Connection | null;
  appSlug?: string;
}
export interface RepositoryChoice extends Connection {
  defaultBranch: string;
}
export interface HistoryEntry {
  sha: string;
  message: string;
  author: string;
  date: string;
}
export const bytes = (value: string): number =>
  new TextEncoder().encode(value).byteLength;
export const isAttachment = (path: string): boolean =>
  /^attachments\/[^/]+\.(?:png|jpe?g|gif|webp)$/i.test(path);
/** An image kept in an encrypted folder's own attachments/ folder. */
export const isEncryptedAttachment = (path: string): boolean =>
  /^(?:[^/]+\/)+attachments\/[^/]+\.(?:png|jpe?g|gif|webp)\.age$/i.test(path);
export const isEncryptedNote = (path: string): boolean => path.endsWith(".md.age");
/** A note or image whose stored text is an age file encrypted to its folder's key. */
export const isEncryptedFile = (path: string): boolean =>
  isEncryptedNote(path) || isEncryptedAttachment(path);
// A key at the repository root would encrypt the whole vault, so only a folder can hold one.
export const isFolderKey = (path: string): boolean =>
  path.endsWith(`/${FOLDER_KEY_NAME}`) && path.length > FOLDER_KEY_NAME.length + 1;
/** Every file stored as ASCII-armored age: encrypted files and the keys that open them. */
export const isAgeFile = (path: string): boolean =>
  isEncryptedFile(path) || isFolderKey(path);
export const isArmoredAge = (text: string): boolean =>
  text.startsWith("-----BEGIN AGE ENCRYPTED FILE-----\n") &&
  text.trimEnd().endsWith("\n-----END AGE ENCRYPTED FILE-----");
export const fileByteLimit = (path: string): number =>
  isAgeFile(path)
    ? MAX_ENCRYPTED_FILE_BYTES
    : isAttachment(path)
      ? MAX_ATTACHMENT_BYTES
      : MAX_NOTE_BYTES;
export function fileByteSize(path: string, text: string): number {
  // Checked wherever sizes are, so plain text can never be stored or committed as an age file.
  if (isAgeFile(path)) {
    if (!isArmoredAge(text))
      throw new VaultError("invalid_vault", `${path} must be an ASCII-armored age file.`);
    return bytes(text);
  }
  if (!isAttachment(path)) return bytes(text);
  if (!text || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(text))
    throw new VaultError("invalid_vault", "Attachment data must be base64.");
  return (text.length / 4) * 3 - (text.endsWith("==") ? 2 : text.endsWith("=") ? 1 : 0);
}
export function assertObject(
  value: unknown,
): asserts value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new VaultError("invalid_request", "Expected an object.");
}
export function validSha(value: unknown): value is string {
  return typeof value === "string" && /^[a-f0-9]{40}$/.test(value);
}
export function sameTarget(
  a: Connection | null,
  b: Connection | null,
): boolean {
  return (
    !!a &&
    !!b &&
    a.repositoryId === b.repositoryId &&
    a.installationId === b.installationId &&
    a.branch === b.branch
  );
}
